// Sistem yönetiminin kabuğu.
//
// Neden ayrı kabuk: `Kabuk` menüsünü rol izin haritasından çiziyor ve süper
// adminin haritası BOŞ (tenant modüllerinin tamamına kapalı, bkz. Rol) — o
// kabuk süper admine boş bir kenar çubuğu, olmayan bir tesis seçicisi ve
// anlamsız bir global arama gösterirdi. Buradaki kabuk aynı design-system
// sınıflarını kullanıyor ama yalnız sistem ekranlarını taşıyor.
//
// Görsel dil kasıtlı olarak AYNI: süper admin de aynı ürünün içinde, farklı
// görünen bir "yönetim aracı"na geçmiyor.

import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DrawerSaglayici, DrawerYuvasi } from '../../components/Drawer'
import { ToastYuvasi } from '../../components/Toast'
import { useOturum } from '../../lib/oturum'

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const

type Oge = { yol: string; baslik: string; ikon: React.ReactNode }

const OGELER: Oge[] = [
  {
    yol: '/sistem',
    baslik: 'Şirketler',
    ikon: (
      <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
      </svg>
    ),
  },
  {
    yol: '/sistem/kutuphane',
    baslik: 'Kütüphane',
    ikon: (
      <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    yol: '/sistem/hesap',
    baslik: 'Hesabım',
    ikon: (
      <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
]

export default function SistemKabuk({ children }: { children: React.ReactNode }) {
  const { durum, cikisYap } = useOturum()
  const yonlendir = useNavigate()
  const konum = useLocation()
  const [menuAcik, setMenuAcik] = useState(false)

  useEffect(() => {
    if (!menuAcik) return
    const kapat = () => setMenuAcik(false)
    document.addEventListener('click', kapat)
    return () => document.removeEventListener('click', kapat)
  }, [menuAcik])

  /**
   * Çıkış. Oturumu burada kapatıp doğrudan /cikis'e gidiyoruz: tenant tarafında
   * araya onay katmanı giriyor (referans `cikisOnay()`), sistem yönetiminde ise
   * kaybolacak bir taslak iş yok — süper admin liste ve kütüphane ekranlarında
   * yarım kalmış bir form bırakmıyor.
   */
  const cikis = (e: React.MouseEvent) => {
    e.preventDefault()
    setMenuAcik(false)
    void cikisYap()
  }

  if (!durum) return null

  const bas = durum.kullanici.ad
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  // Tam eşleşme + alt rota: /sistem/kutuphane açıkken "Şirketler" seçili
  // görünmemeli, ama /sistem/kutuphane/xyz açıkken "Kütüphane" görünmeli.
  const secili = (yol: string) =>
    konum.pathname === yol || (yol !== '/sistem' && konum.pathname.startsWith(`${yol}/`))

  return (
    <DrawerSaglayici>
      <div className="shell">
        <aside className="side">
          <div className="brand">
            <div className="brand-mark">B</div>
            <div className="brand-txt">
              <div className="brand-name">BTYS</div>
              <div className="brand-sub">Sistem Yönetimi</div>
            </div>
          </div>

          <nav className="nav">
            <div className="nav-grp">
              <div className="nav-grp-t">Yönetim</div>
              {OGELER.map((o) => (
                <button
                  key={o.yol}
                  className={secili(o.yol) ? 'nav-i on' : 'nav-i'}
                  type="button"
                  title={o.baslik}
                  onClick={() => yonlendir(o.yol)}
                >
                  {o.ikon}
                  <span className="nav-t">{o.baslik}</span>
                </button>
              ))}
            </div>
          </nav>

          {/*
            Kenar çubuğunda da çıkış: üst sağdaki kullanıcı menüsü tek yol
            olduğunda kullanıcı onu bulamıyordu. Tenant kabuğunda bu yuvada
            "menüyü daralt" duruyor; sistem kabuğunda daraltma yok.
          */}
          <div className="nav-collapse">
            <button className="nav-i" type="button" title="Çıkış yap" onClick={cikis}>
              <svg width="15" height="15" viewBox="0 0 24 24" {...S}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              <span className="nav-t">Çıkış yap</span>
            </button>
          </div>
        </aside>

        {/*
          `.main` gridi dört satır: 46px üst çubuk · 36px SEKME ÇUBUĞU · 1fr
          içerik · 24px durum çubuğu. Sistem kabuğunda sekme çubuğu yok; üç
          çocuk konunca içerik 36px'lik satıra sıkışıp başlık kırpılıyor,
          durum çubuğu da 1fr'lik alanı kaplıyordu. Satırlar bu yüzden burada
          yeniden tanımlanıyor.
        */}
        <div className="main" style={{ gridTemplateRows: '46px 1fr 24px' }}>
          <header className="top">
            <div className="tesis">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#17427E" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <div>
                <b>Sistem Yönetimi</b> <span>· tüm şirketler</span>
              </div>
            </div>

            <div className="top-right">
              <div
                className="user"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuAcik((a) => !a)
                }}
              >
                <div className="avatar">{bas}</div>
                <div className="user-n">
                  <div className="pv-name">{durum.kullanici.ad}</div>
                  <div className="pv-role">Süper Admin</div>
                </div>
                {/* Menünün açıldığını gösteren tek işaret — tenant kabuğunda da var. */}
                <svg width="12" height="12" viewBox="0 0 24 24" {...S}>
                  <path d="m6 9 6 6 6-6" />
                </svg>
                {/*
                  `.umenu` CSS'te `display:none`; yalnız `.on` sınıfıyla açılıyor.
                  Sınıf eklenmediği için menü hiç görünmüyordu ve çıkış
                  ULAŞILAMAZDI. Koşullu render yerine sınıf değişimi kullanılıyor
                  — tenant kabuğundaki kalıbın aynısı.
                */}
                <div className={menuAcik ? 'umenu on' : 'umenu'}>
                  <a
                    href="/sistem/hesap"
                    onClick={(e) => {
                      e.preventDefault()
                      setMenuAcik(false)
                      yonlendir('/sistem/hesap')
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Hesabım
                  </a>
                  <div className="usep" />
                  <a className="dgr" href="/cikis" onClick={cikis}>
                    <svg width="13" height="13" viewBox="0 0 24 24" {...S}>
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                    </svg>
                    Çıkış yap
                  </a>
                </div>
              </div>
            </div>
          </header>

          <main className="content">{children}</main>

          <footer className="statusbar">
            <span>
              <b>BTYS</b> · Sistem Yönetimi
            </span>
            <span className="sep">Tenant ve kütüphane yönetimi</span>
            <span>
              Oturum: <b>{durum.kullanici.ad}</b>
            </span>
          </footer>
        </div>
      </div>

      {/* Çekmece ve toast katmanları — Kabuk'ta da aynı yerde. Yuvalar
          basılmazsa `ac()` çağrısı sessizce hiçbir şey açmıyor. */}
      <DrawerYuvasi />
      <ToastYuvasi />
    </DrawerSaglayici>
  )
}
